import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import { Button, EmptyState, Input, Skeleton, Tag } from "../../../../design-system";
import { useLaunchReadiness } from "../../../../hooks/useLaunchReadiness";
import {
  LaunchCheck,
  LaunchReadiness,
} from "../../../../database/launch/launchReadiness";
import { getAnalyticsEventsOnce } from "../../../../database/analytics/getAnalyticsEvents";
import {
  getActiveUsersInDateRange,
  getPayingActiveUsersInDateRange,
  getTopEvents,
  getUsersByPlatform,
} from "../../../../database/analytics/calculateMetrics";
import usePayingMembers from "../../../../database/analytics/usePayingMembers";
import { getRevenueCatSummary } from "../../../../database/revenuecat/revenueCatApi";
import { RevenueCatSummary } from "../../../../types/RevenueCat";
import { IconKanban } from "../../../../design-system/icons/icons";
import { TONE_DOT } from "./leadUtils";
import {
  ActionsRow,
  Backdrop,
  CloseBtn,
  ErrorText,
  ModalBody,
  ModalCard,
  ModalFoot,
  ModalHead,
  ModalSub,
  ModalTitle,
  Select,
  slugify,
} from "../../board/components/modalKit";
import {
  AppCard,
  AppStageDef,
  APP_RESTING_STAGES,
  DEFAULT_APP_STAGES,
} from "../../../../types/AppPipeline";
import { BoardStageTone } from "../../../../types/Board";
import {
  AppPortfolioSnapshot,
  deleteAppCard,
  getAppPortfolioSnapshot,
  listenToAppCards,
  listenToAppStages,
  moveAppCard,
  seedAppCards,
  setAppStages,
  updateAppCard,
} from "../../../../database/appPipeline/appPipelineApi";
import { useAllCommunities } from "../../../../database/community/getAllCommunities";

// The app portfolio kanban: one card per community app, dragged through
// lifecycle stages (AppPipeline/global). Structural copy of the LeadBoard
// drag-and-drop columns; card content and the drawer are app-specific.
// Operator-only — the parent tab is gated by isBoardOperator.

// ─── styled (board) ──────────────────────────────────────────────────────────

const BoardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
`;

const Hint = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin: 0;
`;

const Scroller = styled.div`
  display: flex;
  gap: var(--space-3);
  overflow-x: auto;
  padding-bottom: var(--space-3);
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: var(--radius-full);
  }
`;

const Column = styled.div<{ $over: boolean }>`
  flex: 0 0 268px;
  width: 268px;
  display: flex;
  flex-direction: column;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$over &&
    css`
      border-color: var(--color-accent);
      box-shadow: 0 0 0 1px var(--color-accent);
    `}
`;

const ColumnHead = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border-bottom: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  z-index: 1;
`;

const ColumnTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Dot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: ${(p) => p.$c};
  flex-shrink: 0;
`;

const ColumnCount = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  background: var(--color-surface-inset);
  padding: 1px var(--space-2);
  border-radius: var(--radius-full);
`;

const ColumnBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  min-height: 56px;
  max-height: calc(100vh - 340px);
  overflow-y: auto;
  flex: 1;
`;

const CardEl = styled.div<{ $dragging: boolean }>`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: grab;
  box-shadow: var(--shadow-xs);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};

  &:hover {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-sm);
  }
  &:active {
    cursor: grabbing;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const CardName = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardNext = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardMuted = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

// Days sitting in the current stage. Warns when an app stalls in a working
// stage; resting stages (live/growth/paused) never warn.
const AgeBadge = styled.span<{ $stale: boolean }>`
  align-self: flex-start;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  flex-shrink: 0;
  ${(p) =>
    p.$stale
      ? css`
          color: var(--color-warning);
          background: var(--color-warning-subtle, var(--color-surface-inset));
        `
      : css`
          color: var(--color-text-tertiary);
          background: var(--color-surface-inset);
        `}
`;

const Empty = styled.div<{ $over: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  text-align: center;
  padding: var(--space-4) var(--space-2);
  border: 1px dashed
    ${(p) => (p.$over ? "var(--color-accent)" : "var(--color-border)")};
  border-radius: var(--radius-md);
`;

// ─── styled (drawer) ─────────────────────────────────────────────────────────

const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: flex-end;
`;

const Drawer = styled.div`
  width: min(440px, 100%);
  height: 100%;
  background: var(--color-surface);
  border-left: 1px solid var(--color-border);
  box-shadow: var(--shadow-lg);
  overflow-y: auto;
  padding: var(--space-6);
`;

const DrawerTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
`;

const DrawerTitle = styled.h2`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin: 0 0 var(--space-2);
`;

const DetailSection = styled.div`
  margin-top: var(--space-5);
`;

const DetailLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-2);
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  resize: vertical;
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const MetaLine = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

// ─── helpers ─────────────────────────────────────────────────────────────────

const daysInStage = (card: AppCard): number => {
  const since = card.stageChangedAt || card.createdAt;
  if (!since) return 0;
  return Math.max(0, moment().diff(moment(since), "days"));
};

const STALE_AFTER_DAYS = 21;

const isStale = (card: AppCard): boolean =>
  !APP_RESTING_STAGES.includes(card.stage) &&
  daysInStage(card) > STALE_AFTER_DAYS;

type StageMaps = {
  byKey: Record<string, AppStageDef>;
  label: (key: string) => string;
  dot: (key: string) => string;
};

const buildStageMaps = (stages: AppStageDef[]): StageMaps => {
  const byKey: Record<string, AppStageDef> = {};
  stages.forEach((s) => (byKey[s.key] = s));
  return {
    byKey,
    label: (key) =>
      byKey[key]?.label ||
      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    dot: (key) => TONE_DOT[byKey[key]?.tone || "neutral"],
  };
};

// A real portfolio app: published (has a store URL) or building toward launch
// (waitlistMode). Keeps the hundreds of demo / outreach-lead communities that
// useAllCommunities returns off the Sync seed.
const isPortfolioApp = (c: any): boolean =>
  !!(c?.iosAppStoreURL || c?.androidPlayStoreURL || c?.waitlistMode);

// Health is derived, never hand-edited: it reads straight off the live
// Community doc. Phase 2a covers only what the client already has (store
// presence, building, landing, active). Revenue / members / launch-readiness /
// build status need a backend snapshot callable — Phase 2b.
type AppHealth = {
  ios: boolean; // live on the App Store
  android: boolean; // live on the Play Store
  building: boolean; // waitlist mode, not yet on a store
  inactive: boolean; // community status flipped off
  landingUrl?: string; // tribed.io/c/{slug}
  iosUrl?: string;
  androidUrl?: string;
};

const deriveHealth = (c: any): AppHealth => {
  const iosUrl = typeof c?.iosAppStoreURL === "string" ? c.iosAppStoreURL : "";
  const androidUrl =
    typeof c?.androidPlayStoreURL === "string" ? c.androidPlayStoreURL : "";
  const slug = typeof c?.slug === "string" && c.slug ? c.slug : c?.id;
  return {
    ios: !!iosUrl,
    android: !!androidUrl,
    building: !iosUrl && !androidUrl && !!c?.waitlistMode,
    inactive: c?.status === "inactive",
    landingUrl: slug ? `https://tribed.io/c/${slug}` : undefined,
    iosUrl: iosUrl || undefined,
    androidUrl: androidUrl || undefined,
  };
};

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

// The derived-health pills. Live-store badges are the headline signal; building
// and inactive are the states worth catching at a glance.
const HealthBadges: React.FC<{
  health: AppHealth;
  extra?: React.ReactNode;
}> = ({ health, extra }) => {
  const pills: { label: string; variant: string }[] = [];
  if (health.ios) pills.push({ label: "iOS", variant: "success" });
  if (health.android) pills.push({ label: "Android", variant: "success" });
  if (health.building) pills.push({ label: "Building", variant: "info" });
  if (health.inactive) pills.push({ label: "Inactive", variant: "danger" });
  if (!pills.length) pills.push({ label: "No store yet", variant: "neutral" });
  return (
    <BadgeRow>
      {pills.map((p) => (
        <Tag key={p.label} variant={p.variant as any} size="sm">
          {p.label}
        </Tag>
      ))}
      {extra}
    </BadgeRow>
  );
};

// Card-face launch badge tone, matching the drawer ring thresholds.
const launchTagVariant = (percent: number): string =>
  percent >= 90 ? "success" : percent >= 50 ? "info" : "warning";

// The snapshot chips sit on a second, compact wrap row under the health pills so
// the card never balloons in height. Same flex-wrap as BadgeRow, tighter top gap.
const SnapRow = styled(BadgeRow)`
  margin-top: 2px;
`;

// GitHub Actions run -> a short build chip. conclusion wins once the run is
// completed; an in-flight run reads as "building".
const buildChip = (
  build: AppPortfolioSnapshot["build"]
): { label: string; variant: string } | null => {
  if (!build) return null;
  const status = (build.status || "").toLowerCase();
  const conclusion = (build.conclusion || "").toLowerCase();
  if (status === "completed") {
    if (conclusion === "success") return { label: "build ok", variant: "success" };
    if (["failure", "cancelled", "timed_out"].includes(conclusion))
      return { label: "build failed", variant: "danger" };
    return { label: `build ${conclusion || "done"}`, variant: "neutral" };
  }
  if (["in_progress", "queued", "waiting", "pending", "requested"].includes(status))
    return { label: "building", variant: "info" };
  return { label: "build", variant: "neutral" };
};

// Info-dense card-face chips from the backend snapshot. Launch falls back to the
// session launchScores cache when the snapshot has not loaded yet, so an already
// opened app still shows its Launch N/M immediately.
const SnapshotChips: React.FC<{
  snap?: AppPortfolioSnapshot;
  launchFallback?: LaunchReadiness["score"];
}> = ({ snap, launchFallback }) => {
  const chips: { key: string; label: string; variant: string }[] = [];

  const launch = snap?.launch || launchFallback || null;
  if (launch) {
    chips.push({
      key: "launch",
      label: `Launch ${launch.ready}/${launch.total}`,
      variant: launchTagVariant(launch.percent),
    });
  }

  const bc = buildChip(snap?.build);
  if (bc) chips.push({ key: "build", label: bc.label, variant: bc.variant });

  const paying = snap?.members?.paying;
  if (typeof paying === "number" && paying > 0)
    chips.push({ key: "paying", label: `${paying} paying`, variant: "neutral" });

  const activity = snap?.activity;
  if (activity) {
    const active = activity.wau || activity.mau;
    if (active > 0)
      chips.push({ key: "active", label: `${active} active`, variant: "neutral" });
  }

  const integ = snap?.integrations;
  if (integ) {
    if (integ.stripeConnected)
      chips.push({ key: "stripe", label: "$", variant: "success" });
    if (integ.revenueCatConfigured)
      chips.push({ key: "rc", label: "RC", variant: "neutral" });
    if (integ.appleCreds === "valid")
      chips.push({ key: "ios-cred", label: "iOS✓", variant: "success" });
    if (integ.googleCreds === "valid")
      chips.push({ key: "and-cred", label: "Android✓", variant: "success" });
    // Only the broken state earns a chip. A healthy or absent Zoom connection
    // stays silent, so the board does not grow a pill per community.
    if (integ.zoomStale)
      chips.push({ key: "zoom", label: "Zoom reconnect", variant: "danger" });
  }

  if (!chips.length) return null;
  return (
    <SnapRow>
      {chips.map((c) => (
        <Tag key={c.key} variant={c.variant as any} size="sm">
          {c.label}
        </Tag>
      ))}
    </SnapRow>
  );
};

// ─── stage editor ────────────────────────────────────────────────────────────

const STAGE_TONES: BoardStageTone[] = [
  "neutral",
  "info",
  "accent",
  "success",
  "warning",
  "danger",
];

const EditorRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 130px auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const ReorderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IconBtn = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  width: 22px;
  height: 18px;
  line-height: 1;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  &:hover:not(:disabled) {
    color: var(--color-text);
    border-color: var(--color-accent);
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const KeyLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  margin-top: 4px;
`;

const KeyInput = styled.input`
  height: 26px;
  width: 150px;
  padding: 0 var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const AppStageEditor: React.FC<{
  initial: AppStageDef[];
  cardCounts: Record<string, number>;
  onClose: () => void;
}> = ({ initial, cardCounts, onClose }) => {
  const [rows, setRows] = useState<AppStageDef[]>(() =>
    initial.map((s) => ({ ...s }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const patch = (i: number, next: Partial<AppStageDef>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...next } : r)));

  const move = (i: number, dir: -1 | 1) =>
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const remove = (i: number) => {
    const row = rows[i];
    const count = cardCounts[row.key] || 0;
    if (
      count > 0 &&
      !window.confirm(
        `${count} app${count === 1 ? "" : "s"} on "${
          row.label || row.key
        }". Deleting the stage keeps those cards in an archived column until you re-stage them. Delete anyway?`
      )
    )
      return;
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setError(null);
    const normalized: AppStageDef[] = rows.map((r) => ({
      key: slugify(r.key || r.label),
      label: r.label.trim(),
      tone: r.tone || "neutral",
    }));
    for (const r of normalized) {
      if (!r.label) return setError("Every stage needs a label.");
      if (!/^[a-z0-9_]+$/.test(r.key))
        return setError(`"${r.label}" has an invalid key.`);
    }
    const keys = normalized.map((r) => r.key);
    const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dupe) return setError(`Duplicate stage key "${dupe}".`);
    if (!normalized.length) return setError("Keep at least one stage.");

    setSaving(true);
    try {
      await setAppStages(normalized);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not save stages.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Backdrop onClick={() => !saving && onClose()}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <ModalTitle>Manage app stages</ModalTitle>
            <ModalSub>
              Order is the lifecycle order, from first contact to live and
              growing.
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={() => !saving && onClose()}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          {rows.map((r, i) => {
            const count = cardCounts[r.key] || 0;
            return (
              <EditorRow key={i}>
                <ReorderCol>
                  <IconBtn
                    type="button"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    aria-label="Move up"
                  >
                    ▲
                  </IconBtn>
                  <IconBtn
                    type="button"
                    disabled={i === rows.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label="Move down"
                  >
                    ▼
                  </IconBtn>
                </ReorderCol>

                <div>
                  <Input
                    value={r.label}
                    placeholder="Stage name"
                    onChange={(e) => patch(i, { label: e.target.value })}
                  />
                  <KeyLine>
                    <Dot $c={TONE_DOT[r.tone || "neutral"]} />
                    <KeyInput
                      value={r.key}
                      placeholder={slugify(r.label) || "key"}
                      onChange={(e) => patch(i, { key: slugify(e.target.value) })}
                      title="Stage key used by automations (lowercase, no spaces)"
                    />
                    {count > 0 && (
                      <Tag variant="neutral" size="sm">
                        {count} app{count === 1 ? "" : "s"}
                      </Tag>
                    )}
                  </KeyLine>
                </div>

                <Select
                  value={r.tone || "neutral"}
                  onChange={(e) =>
                    patch(i, { tone: e.target.value as BoardStageTone })
                  }
                >
                  {STAGE_TONES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>

                <IconBtn
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="Delete stage"
                  style={{ width: 28, height: 28, fontSize: 14 }}
                >
                  ×
                </IconBtn>
              </EditorRow>
            );
          })}

          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setRows((rs) => [...rs, { key: "", label: "", tone: "neutral" }])
              }
            >
              + Add stage
            </Button>
          </div>
          {error && <ErrorText>{error}</ErrorText>}
        </ModalBody>

        <ModalFoot>
          <ModalSub>{rows.length} stages</ModalSub>
          <ActionsRow>
            <Button variant="ghost" size="sm" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" isLoading={saving} onClick={save}>
              Save stages
            </Button>
          </ActionsRow>
        </ModalFoot>
      </ModalCard>
    </Backdrop>
  );
};

// ─── per-app dashboard (drawer) ──────────────────────────────────────────────
// Everything below the health pills is loaded on demand, once, when the drawer
// opens — never fanned out across the board. Launch readiness comes from the
// shared useLaunchReadiness gate (one community); analytics and RevenueCat are
// pulled once here. See project_app_pipeline Phase 2b: up-front card-face
// launch badges for every app need a backend snapshot, not a client fan-out.

const DashGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-2);
`;

const Tile = styled.div`
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TileValue = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-tight);
`;

const TileLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const TileHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  loading?: boolean;
}> = ({ label, value, hint, loading }) => (
  <Tile>
    <TileLabel>{label}</TileLabel>
    {loading ? (
      <Skeleton width="60%" height={22} />
    ) : (
      <TileValue>{value}</TileValue>
    )}
    {hint && <TileHint>{hint}</TileHint>}
  </Tile>
);

// Launch-readiness donut. Value overlaid as HTML so it themes with the rest of
// the UI rather than baking a color into the SVG.
const RingWrap = styled.div`
  position: relative;
  width: 76px;
  height: 76px;
  flex-shrink: 0;
`;

const RingCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-size: var(--font-size-md, 15px);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const percentTone = (percent: number): string =>
  percent >= 90
    ? "var(--color-success)"
    : percent >= 50
      ? "var(--color-accent)"
      : "var(--color-warning)";

const ReadinessRing: React.FC<{ percent: number }> = ({ percent }) => {
  const size = 76;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  const offset = circ * (1 - clamped / 100);
  return (
    <RingWrap>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={percentTone(clamped)}
          strokeWidth={stroke}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <RingCenter>{clamped}%</RingCenter>
    </RingWrap>
  );
};

const LaunchHead = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
`;

const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const CheckText = styled.div`
  min-width: 0;
  flex: 1;
`;

const CheckItem = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const CheckDetail = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 4px 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const EventCount = styled.span`
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
`;

const MAX_BLOCKING = 5;

// The gates still worth fixing: not ready, and actually scored (manual/unknown
// live-store gates are excluded from the checklist score, so they never block).
const blockingChecks = (
  readiness: LaunchReadiness
): (LaunchCheck & { group: string })[] =>
  readiness.groups
    .flatMap((g) => g.checks.map((c) => ({ ...c, group: g.title })))
    .filter(
      (c) => !c.manual && c.status !== "unknown" && c.status !== "ready"
    )
    .sort((a, b) => {
      // Missing before partial, so the hardest gaps surface first.
      const rank = (s: string) => (s === "missing" ? 0 : 1);
      return rank(a.status) - rank(b.status);
    });

const LaunchBlock: React.FC<{ readiness: LaunchReadiness }> = ({
  readiness,
}) => {
  const blocking = blockingChecks(readiness);
  const shown = blocking.slice(0, MAX_BLOCKING);
  const rest = blocking.length - shown.length;
  return (
    <>
      <LaunchHead>
        <ReadinessRing percent={readiness.score.percent} />
        <div>
          <TileValue>
            {readiness.score.ready}/{readiness.score.total}
          </TileValue>
          <TileLabel>launch checks ready</TileLabel>
          {readiness.complete && (
            <div style={{ marginTop: 4 }}>
              <Tag variant="success" size="sm">
                Launch ready
              </Tag>
            </div>
          )}
        </div>
      </LaunchHead>
      {shown.length === 0 ? (
        <TileHint>Every scored launch check is ready.</TileHint>
      ) : (
        <div>
          {shown.map((c) => (
            <CheckRow key={c.key}>
              <Tag
                variant={(c.status === "missing" ? "danger" : "warning") as any}
                size="sm"
              >
                {c.status === "missing" ? "Missing" : "Partial"}
              </Tag>
              <CheckText>
                <CheckItem>{c.item}</CheckItem>
                <CheckDetail>{c.detail}</CheckDetail>
              </CheckText>
            </CheckRow>
          ))}
          {rest > 0 && (
            <TileHint style={{ marginTop: "var(--space-2)" }}>
              +{rest} more open check{rest === 1 ? "" : "s"}
            </TileHint>
          )}
        </div>
      )}
    </>
  );
};

// ─── per-app data hooks (drawer only) ────────────────────────────────────────

type AppAnalyticsState = {
  loading: boolean;
  error: string | null;
  active7: number;
  active30: number;
  topEvents: { event: string; count: number }[];
  platforms: Record<string, number>;
  eventCount: number;
};

const DAY_MS = 86400000;

// Pull this one app's recent events once, then derive the counts client-side.
// Waits for the paying-member gate so the paying/raw active split is correct on
// hard-paywall communities. Bounded (one 30-day window, capped page) — never
// runs for a card that isn't open.
const useAppAnalytics = (
  communityId: string,
  subRequired: boolean,
  subscribedIds: Set<string>,
  membersLoading: boolean
): AppAnalyticsState => {
  const [state, setState] = useState<AppAnalyticsState>({
    loading: true,
    error: null,
    active7: 0,
    active30: 0,
    topEvents: [],
    platforms: {},
    eventCount: 0,
  });

  useEffect(() => {
    // Hold on the loading state until the paying-member gate resolves.
    if (membersLoading) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const now = new Date();
        const start30 = new Date(now.getTime() - 30 * DAY_MS);
        const start7 = new Date(now.getTime() - 7 * DAY_MS);
        const events = await getAnalyticsEventsOnce({
          communityId,
          startDate: start30,
          endDate: now,
          limit: 2000,
        });
        if (cancelled) return;
        const active7 = subRequired
          ? getPayingActiveUsersInDateRange(events, start7, now, subscribedIds)
          : getActiveUsersInDateRange(events, start7, now);
        const active30 = subRequired
          ? getPayingActiveUsersInDateRange(events, start30, now, subscribedIds)
          : getActiveUsersInDateRange(events, start30, now);
        setState({
          loading: false,
          error: null,
          active7,
          active30,
          topEvents: getTopEvents(events, 6),
          platforms: getUsersByPlatform(events),
          eventCount: events.length,
        });
      } catch (e: any) {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            error: e?.message || "Could not load analytics.",
          }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId, subRequired, subscribedIds, membersLoading]);

  return state;
};

type AppRevenueState = {
  loading: boolean;
  error: string | null;
  summary: RevenueCatSummary | null;
};

// RevenueCat overview for this app, once. Returns configured:false (not an
// error) when the community has no RevenueCat credentials.
const useAppRevenue = (communityId: string): AppRevenueState => {
  const [state, setState] = useState<AppRevenueState>({
    loading: true,
    error: null,
    summary: null,
  });
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, summary: null });
    (async () => {
      try {
        const summary = await getRevenueCatSummary(communityId, 5);
        if (!cancelled) setState({ loading: false, error: null, summary });
      } catch (e: any) {
        if (!cancelled)
          setState({
            loading: false,
            error: e?.message || "Could not load revenue.",
            summary: null,
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId]);
  return state;
};

const isCurrencyMetric = (id: string): boolean => /mrr|revenue/i.test(id);

const rcMetricLabel = (
  id: string,
  name: string | null,
  period?: string
): string => {
  // `revenue` is lifetime on the app-scoped path and 28 days on the
  // project-wide path. Older function versions send no period.
  if (id === "revenue") {
    if (period === "last_28_days") return "Revenue (28d)";
    if (period === "lifetime") return "Lifetime revenue";
    return "Revenue";
  }
  const map: Record<string, string> = {
    active_subscriptions: "Active subs",
    active_subscribers: "Active subs",
    active_trials: "Trials",
    mrr: "MRR",
    revenue_28d: "Revenue (28d)",
    new_customers: "New customers",
  };
  return map[id] || name || id;
};

const formatMoney = (value: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value)} ${currency || ""}`.trim();
  }
};

// ─── drawer ──────────────────────────────────────────────────────────────────

const AppDetail: React.FC<{
  card: AppCard;
  displayName: string;
  health: AppHealth;
  community: any;
  stages: AppStageDef[];
  maps: StageMaps;
  snapshot?: AppPortfolioSnapshot;
  onLaunchScore: (communityId: string, score: LaunchReadiness["score"]) => void;
  onClose: () => void;
}> = ({
  card,
  displayName,
  health,
  community,
  stages,
  maps,
  snapshot,
  onLaunchScore,
  onClose,
}) => {
  const launch = useLaunchReadiness(card.communityId, community);
  const paying = usePayingMembers(card.communityId);
  const analytics = useAppAnalytics(
    card.communityId,
    paying.subRequired,
    paying.subscribedIds,
    paying.loading
  );
  const revenue = useAppRevenue(card.communityId);

  // Lift the readiness score up so the card face can show a Launch N/M badge
  // for apps that have been opened, without the board ever fanning out the
  // (callable-heavy) readiness computation across every card.
  useEffect(() => {
    if (launch.readiness) onLaunchScore(card.communityId, launch.readiness.score);
  }, [launch.readiness, card.communityId, onLaunchScore]);

  const rc = revenue.summary;
  const rcConfigured = !!rc?.configured;
  const mrrMetric = rc?.metrics?.find((m) => isCurrencyMetric(m.id));
  const subsMetric = rc?.metrics?.find(
    (m) => m.id === "active_subscriptions" || m.id === "active_subscribers"
  );
  const mrrDisplay =
    rcConfigured && mrrMetric && mrrMetric.value != null
      ? formatMoney(mrrMetric.value, rc!.currency)
      : "—";
  const [nextAction, setNextAction] = useState(card.nextAction);
  const [owner, setOwner] = useState(card.owner);
  const [notes, setNotes] = useState(card.notes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dirty =
    nextAction !== card.nextAction ||
    owner !== card.owner ||
    notes !== card.notes;

  const run = async (fn: () => Promise<void>) => {
    setSaving(true);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveFields = () =>
    run(() => updateAppCard(card.id, { nextAction, owner, notes }));

  const removeCard = () => {
    if (
      !window.confirm(
        `Remove "${displayName}" from the pipeline? The community itself is untouched; Sync apps can re-add it.`
      )
    )
      return;
    run(async () => {
      await deleteAppCard(card.id);
      onClose();
    });
  };

  return (
    <DrawerBackdrop onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()}>
        <DrawerTop>
          <div>
            <DrawerTitle>{displayName}</DrawerTitle>
            <ActionsRow>
              <Tag variant="neutral" size="sm">
                {card.communityId}
              </Tag>
              <Tag
                variant={(maps.byKey[card.stage]?.tone || "neutral") as any}
                size="sm"
              >
                {maps.label(card.stage)}
              </Tag>
              <Tag variant={isStale(card) ? "warning" : "neutral"} size="sm">
                {daysInStage(card)}d in stage
              </Tag>
            </ActionsRow>
          </div>
          <CloseBtn aria-label="Close" onClick={onClose}>
            ×
          </CloseBtn>
        </DrawerTop>

        <DetailSection>
          <DetailLabel>Health</DetailLabel>
          <HealthBadges health={health} />
        </DetailSection>

        {(health.iosUrl || health.androidUrl || health.landingUrl) && (
          <DetailSection>
            <DetailLabel>Links</DetailLabel>
            <ActionsRow>
              {health.iosUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    window.open(health.iosUrl, "_blank", "noopener")
                  }
                >
                  App Store ↗
                </Button>
              )}
              {health.androidUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    window.open(health.androidUrl, "_blank", "noopener")
                  }
                >
                  Play Store ↗
                </Button>
              )}
              {health.landingUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    window.open(health.landingUrl, "_blank", "noopener")
                  }
                >
                  Landing ↗
                </Button>
              )}
            </ActionsRow>
          </DetailSection>
        )}

        <DetailSection>
          <DetailLabel>Launch readiness</DetailLabel>
          {launch.loading && !launch.readiness ? (
            <Skeleton width="100%" height={92} />
          ) : launch.readiness ? (
            <LaunchBlock readiness={launch.readiness} />
          ) : (
            <TileHint>Launch checklist unavailable for this app.</TileHint>
          )}
        </DetailSection>

        <DetailSection>
          <DetailLabel>Analytics</DetailLabel>
          {analytics.loading ? (
            <DashGrid>
              <Skeleton width="100%" height={64} />
              <Skeleton width="100%" height={64} />
            </DashGrid>
          ) : analytics.error ? (
            <TileHint>{analytics.error}</TileHint>
          ) : (
            <>
              <DashGrid>
                <StatTile
                  label={paying.subRequired ? "Paying active (7d)" : "Active (7d)"}
                  value={analytics.active7.toLocaleString()}
                />
                <StatTile
                  label={
                    paying.subRequired ? "Paying active (30d)" : "Active (30d)"
                  }
                  value={analytics.active30.toLocaleString()}
                />
                {paying.subRequired && (
                  <StatTile
                    label="Paying members"
                    value={paying.subscribedIds.size.toLocaleString()}
                    hint="isSubscribed"
                  />
                )}
                <StatTile
                  label="MRR"
                  value={mrrDisplay}
                  loading={revenue.loading}
                  hint={rcConfigured ? "RevenueCat" : "Not connected"}
                />
                {rcConfigured && subsMetric && subsMetric.value != null && (
                  <StatTile
                    label={rcMetricLabel(subsMetric.id, subsMetric.name, subsMetric.period)}
                    value={subsMetric.value.toLocaleString()}
                    hint="RevenueCat"
                  />
                )}
              </DashGrid>
              {revenue.error && !revenue.loading && (
                <TileHint style={{ marginTop: "var(--space-2)" }}>
                  {revenue.error}
                </TileHint>
              )}
              {analytics.topEvents.length > 0 ? (
                <div style={{ marginTop: "var(--space-3)" }}>
                  <TileLabel style={{ marginBottom: "var(--space-1)" }}>
                    Top events (30d)
                  </TileLabel>
                  {analytics.topEvents.map((e) => (
                    <EventRow key={e.event}>
                      <span>{e.event}</span>
                      <EventCount>{e.count.toLocaleString()}</EventCount>
                    </EventRow>
                  ))}
                </div>
              ) : (
                <TileHint style={{ marginTop: "var(--space-2)" }}>
                  No events in the last 30 days.
                </TileHint>
              )}
            </>
          )}
        </DetailSection>

        {snapshot && (snapshot.build || snapshot.integrations) && (
          <DetailSection>
            <DetailLabel>Build & integrations</DetailLabel>
            <BadgeRow>
              {(() => {
                const bc = buildChip(snapshot.build);
                return bc ? (
                  <Tag variant={bc.variant as any} size="sm">
                    {bc.label}
                  </Tag>
                ) : null;
              })()}
              {snapshot.integrations?.stripeConnected && (
                <Tag variant="success" size="sm">
                  Stripe{snapshot.integrations.stripeHasPrices ? " + prices" : ""}
                </Tag>
              )}
              {snapshot.integrations?.revenueCatConfigured && (
                <Tag variant="neutral" size="sm">
                  RevenueCat
                </Tag>
              )}
              {snapshot.integrations?.appleCreds === "valid" && (
                <Tag variant="success" size="sm">
                  Apple creds
                </Tag>
              )}
              {snapshot.integrations?.googleCreds === "valid" && (
                <Tag variant="success" size="sm">
                  Google creds
                </Tag>
              )}
              {snapshot.integrations?.zoomStale && (
                <Tag variant="danger" size="sm">
                  Zoom reconnect
                </Tag>
              )}
            </BadgeRow>
            {snapshot.build?.url && (
              <div style={{ marginTop: "var(--space-2)" }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    window.open(snapshot.build!.url!, "_blank", "noopener")
                  }
                >
                  Build log ↗
                </Button>
              </div>
            )}
          </DetailSection>
        )}

        <DetailSection>
          <DetailLabel>Stage</DetailLabel>
          <Select
            value={card.stage}
            disabled={saving}
            onChange={(e) =>
              run(() => updateAppCard(card.id, { stage: e.target.value }))
            }
          >
            {!maps.byKey[card.stage] && (
              <option value={card.stage}>
                {maps.label(card.stage)} (archived)
              </option>
            )}
            {stages.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
        </DetailSection>

        <DetailSection>
          <DetailLabel>Next action</DetailLabel>
          <Input
            value={nextAction}
            placeholder="The single next move for this app"
            onChange={(e) => setNextAction(e.target.value)}
          />
        </DetailSection>

        <DetailSection>
          <DetailLabel>Owner</DetailLabel>
          <Input
            value={owner}
            placeholder="Who is driving this app"
            onChange={(e) => setOwner(e.target.value)}
          />
        </DetailSection>

        <DetailSection>
          <DetailLabel>Notes</DetailLabel>
          <NotesArea
            value={notes}
            placeholder="Context, blockers, improvement ideas…"
            onChange={(e) => setNotes(e.target.value)}
          />
        </DetailSection>

        <DetailSection>
          <ActionsRow>
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty}
              isLoading={saving}
              onClick={saveFields}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={removeCard}
            >
              Remove from board
            </Button>
          </ActionsRow>
          {error && <ErrorText>{error}</ErrorText>}
        </DetailSection>

        <DetailSection>
          {card.createdAt && (
            <MetaLine>
              Added {moment(card.createdAt).format("MMM D, YYYY")}
            </MetaLine>
          )}
          {card.updatedAt && (
            <MetaLine>Updated {moment(card.updatedAt).fromNow()}</MetaLine>
          )}
        </DetailSection>
      </Drawer>
    </DrawerBackdrop>
  );
};

// ─── add-app picker ──────────────────────────────────────────────────────────

const PickerRow = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-1);
  border-bottom: 1px solid var(--color-border-subtle);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);

  &:hover {
    background: var(--color-surface-subtle);
  }
  &:last-child {
    border-bottom: none;
  }
`;

const PickerName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PickerMuted = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

// Hand-add any community not already on the board — the escape hatch for a won
// app that Sync's portfolio filter skips (no store URL, not in waitlist yet).
// Non-portfolio communities are shown but flagged, so demos aren't added blind.
const AddAppModal: React.FC<{
  addable: { id: string; name: string; portfolio: boolean }[];
  onClose: () => void;
}> = ({ addable, onClose }) => {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return addable;
    return addable.filter(
      (a) =>
        a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q)
    );
  }, [addable, search]);

  const pickedList = addable.filter((a) => picked[a.id]);

  const add = async () => {
    if (!pickedList.length) return;
    setSaving(true);
    setError(null);
    try {
      await seedAppCards(pickedList.map((a) => ({ id: a.id, name: a.name })));
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not add apps.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Backdrop onClick={() => !saving && onClose()}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <ModalTitle>Add app</ModalTitle>
            <ModalSub>
              Any community not already on the board. Communities that aren't
              published or in waitlist mode are flagged "demo?".
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={() => !saving && onClose()}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          <Input
            value={search}
            placeholder="Search name or id…"
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ maxHeight: "48vh", overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <PickerMuted>No communities match.</PickerMuted>
            ) : (
              filtered.map((a) => (
                <PickerRow key={a.id}>
                  <input
                    type="checkbox"
                    checked={!!picked[a.id]}
                    onChange={(e) =>
                      setPicked((p) => ({ ...p, [a.id]: e.target.checked }))
                    }
                  />
                  <PickerName>{a.name}</PickerName>
                  {!a.portfolio && (
                    <Tag variant="warning" size="sm">
                      demo?
                    </Tag>
                  )}
                  <PickerMuted>{a.id}</PickerMuted>
                </PickerRow>
              ))
            )}
          </div>
          {error && <ErrorText>{error}</ErrorText>}
        </ModalBody>

        <ModalFoot>
          <ModalSub>{pickedList.length} selected</ModalSub>
          <ActionsRow>
            <Button variant="ghost" size="sm" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={saving}
              disabled={pickedList.length === 0}
              onClick={add}
            >
              Add {pickedList.length || ""}
            </Button>
          </ActionsRow>
        </ModalFoot>
      </ModalCard>
    </Backdrop>
  );
};

// ─── board ──────────────────────────────────────────────────────────────────

const AppsBoard: React.FC = () => {
  const communities = useAllCommunities();
  const [stages, setStages] = useState<AppStageDef[]>(DEFAULT_APP_STAGES);
  const [cards, setCards] = useState<AppCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingStages, setEditingStages] = useState(false);
  const [dragging, setDragging] = useState<AppCard | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [addingApp, setAddingApp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Launch scores computed by the drawer (one app at a time) and cached for the
  // session, so re-opening an app is instant and the card face can show a
  // Launch N/M badge for apps that have been opened. Board-wide badges up front
  // would need the deferred backend snapshot, not a client fan-out.
  const [launchScores, setLaunchScores] = useState<
    Record<string, LaunchReadiness["score"]>
  >({});
  // Batch portfolio snapshots for every card face, loaded once via one backend
  // call after cards arrive and re-run only when the set of communityIds changes
  // (never on drag) or via the manual Refresh. Never fanned out per card.
  const [snapshots, setSnapshots] = useState<
    Record<string, AppPortfolioSnapshot>
  >({});
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError] = useState<string | null>(null);
  const loadedSnapKey = useRef<string>("");

  const loadSnapshots = useCallback(async (ids: string[]) => {
    if (!ids.length) return;
    setSnapLoading(true);
    setSnapError(null);
    try {
      const next = await getAppPortfolioSnapshot(ids);
      setSnapshots((prev) => ({ ...prev, ...next }));
    } catch (e: any) {
      setSnapError(e?.message || "Could not load app health.");
    } finally {
      setSnapLoading(false);
    }
  }, []);

  const recordLaunchScore = useCallback(
    (communityId: string, score: LaunchReadiness["score"]) => {
      setLaunchScores((prev) => {
        const cur = prev[communityId];
        if (
          cur &&
          cur.ready === score.ready &&
          cur.total === score.total &&
          cur.percent === score.percent
        )
          return prev;
        return { ...prev, [communityId]: score };
      });
    },
    []
  );

  useEffect(() => {
    const unsubStages = listenToAppStages(setStages);
    const unsubCards = listenToAppCards(
      (next) => {
        setCards(next);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => {
      unsubStages();
      unsubCards();
    };
  }, []);

  const maps = useMemo(() => buildStageMaps(stages), [stages]);

  // Live community docs keyed by id — the health badges and drawer links read
  // straight off these (store URLs, waitlist, slug, status), and the live title
  // wins over the seeded snapshot so renames show up.
  const communityById = useMemo(() => {
    const m: Record<string, any> = {};
    communities.forEach((c: any) => {
      if (c?.id) m[c.id] = c;
    });
    return m;
  }, [communities]);

  const nameOf = (card: AppCard) =>
    communityById[card.communityId]?.title ||
    communityById[card.communityId]?.name ||
    card.name ||
    card.communityId;

  const healthOf = (card: AppCard): AppHealth =>
    deriveHealth(communityById[card.communityId]);

  // Portfolio communities with no card yet — what "Sync apps" would add.
  const missing = useMemo(() => {
    const have = new Set(cards.map((c) => c.communityId));
    return communities
      .filter((c: any) => c?.id && !have.has(c.id) && isPortfolioApp(c))
      .map((c: any) => ({ id: c.id, name: c.title || c.name || c.id }));
  }, [communities, cards]);

  // Every community not yet on the board, portfolio or not — the "Add app"
  // picker's source, for hand-adding a won app that Sync's filter skips.
  const addable = useMemo(() => {
    const have = new Set(cards.map((c) => c.communityId));
    return communities
      .filter((c: any) => c?.id && !have.has(c.id))
      .map((c: any) => ({
        id: c.id,
        name: c.title || c.name || c.id,
        portfolio: isPortfolioApp(c),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [communities, cards]);

  const columns = useMemo(() => {
    const orphans = Array.from(new Set(cards.map((c) => c.stage))).filter(
      (k) => k && !maps.byKey[k]
    );
    return [...stages.map((s) => s.key), ...orphans];
  }, [cards, stages, maps]);
  const isOrphan = (stage: string) => !maps.byKey[stage];

  const byStage = useMemo(() => {
    const map: Record<string, AppCard[]> = {};
    columns.forEach((s) => (map[s] = []));
    cards.forEach((c) => (map[c.stage] || (map[c.stage] = [])).push(c));
    columns.forEach((s) => map[s].sort((a, b) => a.position - b.position));
    return map;
  }, [cards, columns]);

  const cardCounts = useMemo(() => {
    const m: Record<string, number> = {};
    cards.forEach((c) => (m[c.stage] = (m[c.stage] || 0) + 1));
    return m;
  }, [cards]);

  // Stable key of the community set on the board. Drag reorders/moves stages but
  // never changes this, so the auto-load fires once and does not re-run on drag.
  const communityIdsKey = useMemo(
    () =>
      Array.from(new Set(cards.map((c) => c.communityId)))
        .sort()
        .join(","),
    [cards]
  );

  useEffect(() => {
    if (!communityIdsKey) return;
    if (loadedSnapKey.current === communityIdsKey) return;
    loadedSnapKey.current = communityIdsKey;
    loadSnapshots(communityIdsKey.split(","));
  }, [communityIdsKey, loadSnapshots]);

  const refreshSnapshots = () =>
    loadSnapshots(communityIdsKey ? communityIdsKey.split(",") : []);

  const selected = selectedId
    ? cards.find((c) => c.id === selectedId) || null
    : null;

  const sync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await seedAppCards(missing);
    } catch (e: any) {
      setError(e?.message || "Could not sync apps.");
    } finally {
      setSyncing(false);
    }
  };

  const endDrag = () => {
    setDragging(null);
    setOverStage(null);
  };

  const handleDrop = (stage: string) => {
    if (dragging && dragging.stage !== stage && !isOrphan(stage)) {
      moveAppCard(dragging.id, stage, Date.now()).catch((e) =>
        setError(e?.message || "Could not move the card.")
      );
    }
    endDrag();
  };

  if (loading) {
    return (
      <div>
        <Skeleton width="30%" height={18} />
        <div style={{ marginTop: "var(--space-4)" }}>
          <Skeleton width="100%" height={220} />
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <>
        <EmptyState
          icon={<IconKanban size={20} />}
          title="No apps on the board yet"
          description="Sync pulls in your real apps (live on a store or building toward launch) into the first column; from there, drag each to its real stage."
        />
        <div style={{ textAlign: "center", marginTop: "var(--space-3)" }}>
          <ActionsRow style={{ justifyContent: "center" }}>
            <Button
              variant="primary"
              size="sm"
              isLoading={syncing}
              disabled={missing.length === 0}
              onClick={sync}
            >
              Sync apps ({missing.length})
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAddingApp(true)}>
              Add app
            </Button>
          </ActionsRow>
        </div>
        {error && <ErrorText>{error}</ErrorText>}
        {addingApp && (
          <AddAppModal addable={addable} onClose={() => setAddingApp(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <BoardHead>
        <Hint>
          One card per app. Drag to change its lifecycle stage; click for next
          action and notes.
        </Hint>
        <ActionsRow>
          {missing.length > 0 && (
            <Button variant="ghost" size="sm" isLoading={syncing} onClick={sync}>
              Sync apps ({missing.length})
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            isLoading={snapLoading}
            onClick={refreshSnapshots}
          >
            Refresh data
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAddingApp(true)}>
            Add app
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditingStages(true)}
          >
            Manage stages
          </Button>
        </ActionsRow>
      </BoardHead>
      {error && <ErrorText>{error}</ErrorText>}
      {snapError && <ErrorText>{snapError}</ErrorText>}
      <Scroller>
        {columns.map((stage) => {
          const items = byStage[stage] || [];
          const orphan = isOrphan(stage);
          const over = overStage === stage && !orphan;
          return (
            <Column
              key={stage}
              $over={over}
              onDragOver={(e) => {
                if (!dragging || orphan) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overStage !== stage) setOverStage(stage);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setOverStage((s) => (s === stage ? null : s));
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(stage);
              }}
            >
              <ColumnHead>
                <ColumnTitle>
                  <Dot
                    $c={orphan ? "var(--color-text-tertiary)" : maps.dot(stage)}
                  />
                  {maps.label(stage)}
                  {orphan && (
                    <Tag variant="neutral" size="sm">
                      archived
                    </Tag>
                  )}
                </ColumnTitle>
                <ColumnCount>{items.length}</ColumnCount>
              </ColumnHead>
              <ColumnBody>
                {items.length === 0 ? (
                  <Empty $over={over}>{over ? "Drop here" : "—"}</Empty>
                ) : (
                  items.map((c) => (
                    <CardEl
                      key={c.id}
                      $dragging={dragging?.id === c.id}
                      draggable
                      onClick={() => setSelectedId(c.id)}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", c.id);
                        setDragging(c);
                      }}
                      onDragEnd={endDrag}
                    >
                      <CardTop>
                        <CardName>{nameOf(c)}</CardName>
                        <AgeBadge $stale={isStale(c)}>
                          {daysInStage(c)}d
                        </AgeBadge>
                      </CardTop>
                      <HealthBadges health={healthOf(c)} />
                      <SnapshotChips
                        snap={snapshots[c.communityId]}
                        launchFallback={launchScores[c.communityId]}
                      />
                      {c.nextAction ? (
                        <CardNext>{c.nextAction}</CardNext>
                      ) : (
                        <CardMuted>No next action</CardMuted>
                      )}
                    </CardEl>
                  ))
                )}
              </ColumnBody>
            </Column>
          );
        })}
      </Scroller>

      {selected && (
        <AppDetail
          card={selected}
          displayName={nameOf(selected)}
          health={healthOf(selected)}
          community={communityById[selected.communityId]}
          stages={stages}
          maps={maps}
          snapshot={snapshots[selected.communityId]}
          onLaunchScore={recordLaunchScore}
          onClose={() => setSelectedId(null)}
        />
      )}

      {editingStages && (
        <AppStageEditor
          initial={stages}
          cardCounts={cardCounts}
          onClose={() => setEditingStages(false)}
        />
      )}

      {addingApp && (
        <AddAppModal addable={addable} onClose={() => setAddingApp(false)} />
      )}
    </>
  );
};

export default AppsBoard;
export { AppsBoard };
